import React from 'react';
import { StacklineColor, hexToRgba, useStacklineTheme } from '@stackline/ui';
import { Link } from 'react-router-dom';
import { useQueryParamValue, useMetricFormatter, useAppSelector } from 'src/utils/Hooks';
import { addPersistentQueryParams } from 'src/utils/browser';
import { getProductImageUrl } from 'src/utils/image';
import { formatTime } from 'src/utils/dateformatting';
import ArrowRight from '!svg-react-loader!../ProductGrowthMatrixCard/assets/arrowRight.svg'; // eslint-disable-line
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import ReviewStarsV2 from 'src/components/BeaconV2Components/ProductReviewCard/StarsV2';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import styled from '@emotion/styled';
import Truncate from 'react-truncate';
import StatusTag from 'src/components/BeaconV2Components/StatusTag/StatusTag';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  ReviewPopupState,
  ReviewType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/Visualizations/ProductReviewGrid';
import colors from 'src/utils/colors';

// design team wanted to have the perfect circle when it is small value
const MIN_PROGRESS_VALUE = 7;

interface ProductReviewCardV2Props {
  review: ReviewType;
  setReviewPopup: React.Dispatch<React.SetStateAction<ReviewPopupState>>;
}

const StyledBorderLinearProgress = styled(LinearProgress)({
  height: 10,
  width: '149px',
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: hexToRgba(colors.lightGrey, 0.4)
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: colors.darkBlue
  }
});

const SentimentStatusMapper = {
  positive: { color: 'success', displayName: 'Positive' },
  neutral: { color: 'accentGold', displayName: 'Neutral' },
  negative: { color: 'error', displayName: 'Negative' }
};

/**
 *  Small Progress bar, label and value that will be placed on review card
 */
const SentimentProgressBar = ({ value, title }: { value: number; title: string }) => {
  const metricFormatter = useMetricFormatter();
  const progressValue = value * 100 < MIN_PROGRESS_VALUE && value * 100 !== 0 ? MIN_PROGRESS_VALUE : value * 100;

  return (
    <>
      <Flex width="33px" alignItems="center" justifyContent="end">
        <Text variant="subtitle3">
          {metricFormatter(value, METRICTYPE.PERCENT, {
            showFullValue: true,
            decimalPlaces: 1
          })}
        </Text>
      </Flex>
      <div style={{ marginLeft: '24px' }}>
        <StyledBorderLinearProgress variant="determinate" value={progressValue} />
      </div>
      <Flex marginLeft="24px">
        <Text variant="body3">{title}</Text>
      </Flex>
    </>
  );
};

/**
 * ProductReviewCard Component
 *
 * Displays a card with information about a product review, including the product image,
 * brand, product name, date, customer, rating, and customer review text. Provides a link
 * to view more details about the product (redirect to product level).
 * Quality Score and Sentiment analysis
 */
const ProductReviewCardV2: React.FC<ProductReviewCardV2Props> = ({ review, setReviewPopup }) => {
  const { product, stacklineSku, user, reviewTime, reviewText, sentimentType } = review;

  const theme = useStacklineTheme();
  const tab = useQueryParamValue('tab');
  const subTab = useQueryParamValue('subtab');
  const linkedSubtab = tab !== 'reviews' ? 'reviewMetrics' : subTab;
  const formatMetric = useMetricFormatter();
  const { retailer, mainTimePeriod, app } = useAppSelector((state) => state);
  const { targetUrl, name } = app;
  const { additionalParams, searchParams } = useAppSelector((state) => state.app.queryParams);

  const queryParams = `${addPersistentQueryParams(retailer, mainTimePeriod)}&tab=reviews&subtab=${linkedSubtab}`;
  const productUrl = `/product/${stacklineSku}${searchParams}${additionalParams}`;
  return (
    <Flex
      flexDirection="column"
      style={{
        width: '512px',
        padding: `24px 16px 20px 16px`,
        borderRadius: '12px',
        border: `1px solid ${theme.colors.primaryGray}`
      }}
    >
      <Flex flexDirection="column">
        {/* Image, Brand and Product Name */}
        <Flex justifyContent="flex-start" alignItems="flex-start">
          {/* Image */}
          <Link to={productUrl} onClick={(event) => event.stopPropagation()}>
            <img
              style={{
                height: '80px',
                maxWidth: '80px',
                objectFit: 'contain'
              }}
              src={getProductImageUrl(stacklineSku)}
              alt={product.title}
            />
          </Link>

          {/* Brand Name Product Name */}
          <Flex flexDirection="column" marginLeft="16px" justifyContent="center">
            <Flex height="24px" alignItems="center">
              <Link to={`/brand/${product.brandId}?${queryParams}`}>
                <Text variant="subtitle1">{product.brandName}</Text>
              </Link>
            </Flex>
            <Flex marginTop="8px" height="40px">
              <Link to={productUrl}>
                <Text lineHeight="20px" variant="body2" truncateLines={2}>
                  {product.title}
                </Text>
              </Link>
            </Flex>
          </Flex>
        </Flex>

        {/* Stars, Date, Customer and Review */}
        <Flex
          padding="12px"
          marginTop="16px"
          sx={{
            border: `1px solid ${theme.colors.secondaryPorcelain}`,
            borderRadius: '8px',
            backgroundColor: hexToRgba(theme.colors.secondaryPorcelain, 0.5)
          }}
          flexDirection="column"
        >
          <Flex gap={64}>
            {/* Ratings */}
            <Flex width="85px" flexDirection="column">
              <Flex height="16px">
                <Text transform="uppercase" variant="subtitle4">
                  RATING
                </Text>
              </Flex>
              <Flex height="19px" marginTop="8px">
                <ReviewStarsV2 review={review} />
              </Flex>
            </Flex>

            {/* Date */}
            <Flex width="85px" flexDirection="column">
              <Flex>
                <Text transform="uppercase" variant="subtitle4">
                  Date
                </Text>
              </Flex>
              <Flex marginTop="8px">
                <Text variant="body2">{formatTime(reviewTime, 'MMM D, YYYY')}</Text>
              </Flex>
            </Flex>

            {/* Customer */}
            <Flex width="100px" flexDirection="column">
              <Flex>
                <Text transform="uppercase" variant="subtitle4">
                  Customer
                </Text>
              </Flex>
              <Flex marginTop="8px" width="167px">
                <Text variant="body2" truncateLines={1}>
                  {user.username}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          {/* Actual Review */}
          <Flex marginTop="16px" flexDirection="column">
            <Flex flexDirection="column">
              <Text transform="uppercase" variant="subtitle4">
                Title
              </Text>
              <Text sx={{ marginTop: '2px' }} variant="body2" truncateLines={1}>
                {review.reviewTitle}
              </Text>
            </Flex>
            <Flex height="16px" alignItems="center" marginTop="16px">
              <Text transform="uppercase" variant="subtitle4">
                Description
              </Text>
            </Flex>
            <Flex marginTop="2px" height="57px">
              <Truncate
                style={{
                  WebkitFontSmoothing: 'antialiased'
                }}
                lines={3}
                ellipsis={
                  <span>
                    ...
                    <Text
                      onClick={() => {
                        setReviewPopup({ review, open: true });
                      }}
                      variant="subtitle2"
                      inline
                      link
                      underlined
                      sx={{ marginLeft: '4px' }}
                    >
                      View more
                    </Text>
                  </span>
                }
              >
                {reviewText}
              </Truncate>
            </Flex>
          </Flex>
        </Flex>

        {/* QUALITY SCORE AND SENTIMENT TAG */}
        <Flex
          flexDirection="column"
          padding="12px"
          marginTop="12px"
          sx={{
            border: `1px solid ${theme.colors.secondaryPorcelain}`,
            borderRadius: '8px',
            backgroundColor: hexToRgba(theme.colors.secondaryPorcelain, 0.5)
          }}
        >
          <Flex>
            {/* Content Score overview */}
            <Flex flexDirection="column" width="230px">
              <Flex height="16px">
                <Text variant="subtitle3" transform="uppercase">
                  Quality Score
                </Text>
              </Flex>
              <Flex height="26px" marginTop="12px">
                <Text sx={{ fontWeight: 500, fontSize: '20px' }}>
                  {formatMetric(review.reviewQualityScore, METRICTYPE.PERCENT, {
                    showFullValue: true,
                    decimalPlaces: 1
                  })}
                </Text>
              </Flex>
            </Flex>

            {/* Sentiment tag */}
            <Flex flexDirection="column">
              <Flex height="16px">
                <Text variant="subtitle3" transform="uppercase">
                  Sentiment tag
                </Text>
              </Flex>

              <Flex height="26px" marginTop="12px">
                <StatusTag
                  color={
                    (SentimentStatusMapper[sentimentType]?.color as StacklineColor) ?? ('accentGold' as StacklineColor)
                  }
                  backgroundColor={
                    (SentimentStatusMapper[sentimentType]?.color as StacklineColor) ?? ('accentGold' as StacklineColor)
                  }
                >
                  {SentimentStatusMapper[sentimentType]?.displayName}
                </StatusTag>
              </Flex>
            </Flex>
          </Flex>

          {/* Content Graph Area */}
          <Flex marginTop="18px" flexDirection="column">
            <Flex alignItems="center">
              <SentimentProgressBar value={review.wordCountQualityScore} title="Word count" />
            </Flex>

            <Flex marginTop="12px" alignItems="center">
              <SentimentProgressBar value={review.keywordCountQualityScore} title="Keyword count" />
            </Flex>

            <Flex marginTop="12px" alignItems="center">
              <SentimentProgressBar value={review.imageScore} title="Image score" />
            </Flex>

            <Flex marginTop="12px" alignItems="center">
              <SentimentProgressBar value={review.videoScore} title="Video score" />
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      {/* CONTENT SCORE BAR CHART */}

      {/* View more button and arrow */}

      <Flex marginTop="16px">
        <Link
          to={{
            pathname: `https://${targetUrl}/api/utility/OpenRetailerProductReviewPage?appName=${name}&retailerId=${retailer.id}&stacklineSku=${review.stacklineSku}&reviewId=${review.reviewId}`
          }}
          target="_blank"
        >
          <Text variant="subtitle4" link>
            View details
          </Text>
        </Link>

        <ArrowRight
          style={{
            marginLeft: '4px',
            position: 'relative',
            top: '2px'
          }}
        />
      </Flex>
    </Flex>
  );
};

export default ProductReviewCardV2;
